import type React from 'react';
import { useEffect, useState } from 'react';
import { openNotification } from 'components/notification';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'components/loadingbutton/LoadingButton';
import Check from '@mui/icons-material/Check';
import Link from '@mui/icons-material/Link';
import { useUpdateDocumentStatus } from 'hooks/useUpdateDocumentStatus';
import { DocumentStatus } from 'services/documents/entities';

interface CopyLinkButtonProps {
  contentId: string;
  documentStatus: DocumentStatus;
  previewURL: string;
}

export const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({ contentId, documentStatus, previewURL }) => {
  const { t } = useTranslation();
  const [isButtonCopied, setIsButtonCopied] = useState<boolean>(false);
  const [buttonLabel, setButtonLabel] = useState<string>(t('share_document_v3.copy_link_button.copy_link'));

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (buttonLabel === t('share_document_v3.copy_link_button.link_copied')) {
      timeout = setTimeout(() => {
        setButtonLabel(t('share_document_v3.copy_link_button.copy_link'));
        setIsButtonCopied(false);
      }, 3000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [buttonLabel]);

  const { updateStatus, isLoading: isDocumentStatusLoading } = useUpdateDocumentStatus({
    contentId,
    onSuccess: () => {
      setButtonLabel(t('share_document_v3.copy_link_button.link_copied'));
      setIsButtonCopied(true);
      openNotification({
        type: 'success',
        title: t('share_document_v3.share_card.document_status_response', { status: 'Sent' }),
      });
    },
    onError: () => {
      openNotification({
        type: 'error',
        title: t('documents.update_status_error_message_title'),
        description: (
          <>
            {t('documents.update_status_error_message_part_1')}
            <a href="mailto:support@proposify.com">{t('documents.update_status_error_message_part_2')}</a>
          </>
        ),
      });
    },
  });

  const handleCopyLink = () => {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const fullUrl = new URL(previewURL, baseUrl).toString();
    navigator.clipboard.writeText(fullUrl);
    setButtonLabel(t('share_document_v3.copy_link_button.link_copied'));
    setIsButtonCopied(true);
    if (documentStatus === DocumentStatus.DRAFT || documentStatus === DocumentStatus.APPROVED) {
      updateStatus('Sent');
    }
  };

  return (
    <LoadingButton
      variant="contained"
      color="primary"
      startIcon={isButtonCopied ? <Check /> : <Link />}
      onClick={handleCopyLink}
      isLoading={isDocumentStatusLoading}
    >
      {buttonLabel}
    </LoadingButton>
  );
};
